import React, { useState, useEffect } from "react";
import { AddPropertyForm } from "./AddPropertyPage";
import PropertyTypeModal from "../../../components/properties/PropertyTypeModal";
import { updateProperty, getProperty } from "../../../api/agent";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { invalidateCache } from "../../../lib/cache";

const getInitialStatusForListingType = (type) => {
    if (type === "appraisal") return "Appraisal";
    if (type === "offmarket") return "Off Market Listing";
    if (type === "onmarket") return "On Market Listing";
    return "On Market Listing";
};

export default function AddPublicPropertyPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isUpdating, setIsUpdating] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [isLoading, setIsLoading] = useState(!!id);
    const [showTypeModal, setShowTypeModal] = useState(!id);
    const [selectedListingType, setSelectedListingType] = useState(null);

    useEffect(() => {
        if (id) {
            const fetchProperty = async () => {
                try {
                    setIsLoading(true);
                    // Use LMS endpoint (getProperty) instead of REA Crawler endpoint
                    const res = await getProperty(id);
                    const data = res.data;

                    // Transform for form - matching AddPropertyPage structure
                    const ownerData = data.owner || {};
                    const formValues = {
                        title: data.title || "",
                        address: data.address || "",
                        suburb: data.suburb || data.city || "",
                        city: data.suburb || data.city || "",
                        state: data.state || "",
                        postal_code: data.postal_code || "",
                        price: data.price ?? "",
                        beds: data.beds ?? "",
                        baths: data.baths ?? "",
                        parkings: data.parkings ?? "",
                        car_ports: data.car_ports ?? "",
                        open_spaces: data.open_spaces ?? "",
                        area: data.area ?? "",
                        description: data.description || "",
                        status: data.status?.name || data.status || "On Market Listing",
                        propertyTypes: data.property_type || "House",
                        images: data.images || [],
                        // Map Owner/Agent info correctly - use owner object from API
                        owner: {
                            name: ownerData.name || data.owner_name || "",
                            preferred_name: ownerData.preferred_name || "",
                            contact: ownerData.contact || data.owner_contact || "",
                            email: ownerData.email || data.owner_email || ""
                        },
                        additionalOwners: data.additional_owners || [],
                        // Internal/External features
                        internal_features: data.internal_features || "",
                        external_features: data.external_features || "",
                        // Weekly rent and Industry Standards
                        weekly_rent: data.weekly_rent != null ? data.weekly_rent : "",
                        current_annual_property_management_fee: data.current_annual_property_management_fee != null ? data.current_annual_property_management_fee : "",
                        // Ensure other fields are mapped correctly
                        method_of_sale: data.method_of_sale || "Private Sale",
                        commission_type: data.commission_type || (data.commission_amount != null && data.commission_amount !== "" ? "fixed" : "percentage"),
                        commission_rate: data.commission_rate ?? "",
                        commission_amount: data.commission_amount != null && data.commission_amount !== "" ? `$${Number(data.commission_amount).toLocaleString()}` : "",
                        parking_type: data.parking_type || "",
                        parking_spaces: data.parking_spaces ?? "",
                        floor_area: data.floor_area || data.building_size || "",
                        land_area: data.land_size || data.land_area || "",
                        building_size: data.building_size || data.floor_area || "",
                        year_built: data.year_built || "",
                        property_class: data.property_class || "",
                        // Yield & Financial Details
                        est_gross_yield: data.est_gross_yield || "",
                        est_outgoings: data.est_outgoings || "",
                        est_net_yield: data.est_net_yield || "",
                        // Annual Outgoings Breakdown
                        body_corporate: data.body_corporate || "",
                        council_rates: data.council_rates || "",
                        water_rates: data.water_rates || "",
                        // Publishing status
                        published_on_website: data.published_on_website,
                        published_on_rea: data.published_on_rea,
                        published_on_domain: data.published_on_domain,
                        // Auction details
                        auction_date: data.auction_date || "",
                        auction_venue: data.auction_venue || "",
                        // Media URLs
                        virtual_tour_link: data.virtual_tour_link || "",
                        video_url: data.video_url || "",
                        // Transaction details
                        transaction_type: data.transaction_type || "sale",
                        price_display: data.price_display || "exact",
                        secondaryAgent: data.co_agent?.id ? String(data.co_agent.id) : "",
                        agentId: data.agent?.id ? String(data.agent.id) : "",
                    };

                    setInitialValues({
                        ...formValues,
                        published_on_collings: data.published_on_collings || false,
                        appraisal_market_type: data.appraisal_market_type || "",
                        appraisal_temperature: data.appraisal_temperature || "warm",
                    });
                } catch (error) {
                    console.error("Fetch error:", error);
                    toast.error("Failed to load property details");
                } finally {
                    setIsLoading(false);
                }
            };
            fetchProperty();
        }
    }, [id]);

    const handleSuccessAndClose = () => {
        invalidateCache.properties();
        invalidateCache.publicProperties();
        navigate("/public-property-search");
    };

    const handleSubmit = async (propertyData, formData) => {
        try {
            setIsUpdating(true);
            // Edit only: create uses form's internal registerProperty → documents step
            await updateProperty(id, formData);
            toast.success("Property updated successfully!");
        } catch (error) {
            console.error("Save error:", error);
            const errorMessage = error?.response?.data?.detail || error.message || "Failed to save property";
            toast.error(errorMessage);
            throw error;
        } finally {
            setIsUpdating(false);
        }
    };

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    return (
        <>
            {!id && (
                <PropertyTypeModal
                    isOpen={showTypeModal}
                    onClose={() => navigate("/public-property-search")}
                    onSelectType={(type) => {
                        setSelectedListingType(type);
                        setShowTypeModal(false);
                    }}
                />
            )}
            {((id) || selectedListingType != null) && (
                <AddPropertyForm
                    onSubmit={id ? handleSubmit : undefined}
                    isUpdating={isUpdating}
                    isSuccess={handleSuccessAndClose}
                    onClose={handleSuccessAndClose}
                    isCrossIconPress={handleSuccessAndClose}
                    listingType={id ? undefined : selectedListingType}
                    hideFurnished
                    initialValues={
                        id
                            ? initialValues
                            : selectedListingType != null
                                ? { status: getInitialStatusForListingType(selectedListingType) }
                                : null
                    }
                    isEdit={!!id}
                />
            )}
        </>
    );
}
